import { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TangramIcon } from "@/components/TangramIcon";
import { Loader2, Clock3, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

const ROLES = [
  { value: "staff_obra", label: "Staff obra" },
  { value: "staff_oficina", label: "Staff oficina central" },
  { value: "asset_admin", label: "Administrador de activos" },
];

export default function Register() {
  const { user, register } = useAuth();
  const [form, setForm] = useState({ company_code: "", name: "", alias: "", email: "", password: "", role: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [err, setErr] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.role) { toast.error("Selecciona un rol"); return; }
    setSubmitting(true);
    setErr("");
    try {
      await register({ ...form, company_code: form.company_code.trim().toUpperCase() });
      setDone(true);
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || e.message;
      setErr(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  // Post-registration status screen — "en proceso de alta"
  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F5F1EA] px-4 py-12">
        <div className="w-full max-w-md bg-white border border-[#E2E8F0] p-8 rounded-sm text-center space-y-5" data-testid="register-pending">
          <div className="flex items-center justify-center gap-2.5 text-[#0A2540]">
            <TangramIcon size={22} outline stroke="#0A2540" />
            <span className="font-display text-lg font-extrabold tracking-tight">TANGRAM</span>
          </div>
          <div className="w-16 h-16 rounded-full bg-[#F59E0B]/15 flex items-center justify-center mx-auto">
            <Clock3 size={30} className="text-[#B45309]" />
          </div>
          <h2 className="font-display text-2xl font-extrabold tracking-tighter text-[#0A2540]">En proceso de alta</h2>
          <p className="text-sm text-[#5C6A7D]">
            Tu registro fue recibido y está <strong>en espera de validación</strong> por el administrador de tu compañía. Te avisaremos cuando tu cuenta sea activada; hasta entonces no podrás iniciar sesión.
          </p>
          <Link to="/login">
            <Button className="w-full bg-[#0A2540] hover:bg-[#173B63] text-white rounded-sm h-11" data-testid="back-to-login">Volver al inicio de sesión</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F5F1EA] px-4 py-12">
      <form onSubmit={onSubmit} className="w-full max-w-md bg-white border border-[#E2E8F0] p-8 rounded-sm space-y-5" data-testid="register-form">
        <div className="flex items-center gap-2.5 text-[#0A2540]">
          <TangramIcon size={22} outline stroke="#0A2540" />
          <span className="font-display text-lg font-extrabold tracking-tight">TANGRAM</span>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#5C6A7D]">Crear cuenta</div>
          <h2 className="font-display text-3xl font-extrabold tracking-tighter text-[#0A2540] mt-1">Únete a tu compañía</h2>
          <p className="text-xs text-[#5C6A7D] mt-2">Solo empleados de compañías registradas pueden crear cuenta. Ingresa el código que te dio tu administrador.</p>
        </div>

        {err && <div className="text-sm text-[#E11D48] bg-[#E11D48]/8 border border-[#E11D48]/20 px-3 py-2 rounded-sm" data-testid="register-error">{err}</div>}

        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-[0.18em] text-[#5C6A7D]">Código de compañía</Label>
          <Input required value={form.company_code} onChange={(e) => setForm({ ...form, company_code: e.target.value })} className="rounded-sm h-11 uppercase" placeholder="Ej. 01A" data-testid="register-company-code" />
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-[0.18em] text-[#5C6A7D]">Nombre completo</Label>
          <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm h-11" data-testid="register-name" />
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-[0.18em] text-[#5C6A7D]">Alias <span className="text-[#94A3B8] normal-case tracking-normal">(opcional)</span></Label>
          <Input value={form.alias} onChange={(e) => setForm({ ...form, alias: e.target.value })} className="rounded-sm h-11" placeholder="Cómo te verán en la app" data-testid="register-alias" />
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-[0.18em] text-[#5C6A7D]">Correo</Label>
          <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-sm h-11" data-testid="register-email" />
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-[0.18em] text-[#5C6A7D]">Contraseña</Label>
          <div className="relative">
            <Input
              required type={showPassword ? "text" : "password"} minLength={6}
              value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="rounded-sm h-11 pr-11" placeholder="Mínimo 6 caracteres" data-testid="register-password"
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
              className="absolute inset-y-0 right-2 flex items-center justify-center w-8 text-[#5C6A7D] hover:text-[#0A2540] transition-colors"
              data-testid="register-password-toggle"
            >
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-[0.18em] text-[#5C6A7D]">Rol</Label>
          <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
            <SelectTrigger className="rounded-sm h-11" data-testid="register-role"><SelectValue placeholder="Selecciona tu rol" /></SelectTrigger>
            <SelectContent>
              {ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <Button type="submit" disabled={submitting} className="w-full bg-[#0A2540] hover:bg-[#173B63] text-white rounded-sm h-11 font-semibold" data-testid="register-submit">
          {submitting ? <Loader2 className="animate-spin" size={16} /> : "Crear cuenta"}
        </Button>

        <p className="text-sm text-[#5C6A7D]">¿Ya tienes cuenta? <Link to="/login" className="text-[#0052CC] font-semibold" data-testid="login-link">Inicia sesión</Link></p>
      </form>
    </div>
  );
}
